/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';
import statutsConfig from '../../../../config/statuts.json';
import type { Personnel } from '../../../../shared/types/game';
import { fetchBuildingPersonnel } from '../../components/BuildingDetailModal/buildingDetailHelpers';
import {
  startOfLocalDay,
  startOfWeekMonday,
  addLocalDays,
  toLocalDateInputValue,
  parseLocalDateInput,
  formatWeekBannerFr,
  formatDayHeaderFr,
  parsePlanningWeeksJson,
} from '../../components/operationalPlanningHelpers';

/**
 * Planning du site — port fidèle du prototype (`network.js` planningPage) :
 * trois sous-onglets « Trames de groupe / Grille du personnel / Répartition des
 * groupes ». Chaque groupe A-D porte une trame hebdomadaire ; les agents en
 * héritent (sauf décision individuelle). Visuel .oscockpit, branché DB
 * (`/buildings/:id/trame` + personnel du bâtiment).
 */

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const JOUR_COURANT = (new Date().getDay() + 6) % 7; // Lun=0 … Dim=6
const GROUPES = ['A', 'B', 'C', 'D'];
const DEFAUT = 'PERSONNELS';

interface PS {
  nom: string;
  code: string;
  couleurs?: { backgroundColor?: string; textColor?: string };
}
const PLANNING_STATUTS: PS[] = (() => {
  const p = (statutsConfig as any)?.statuts_planning?.Pompiers;
  const list: PS[] = Array.isArray(p)
    ? p.map((s: any) => ({ nom: String(s?.nom || ''), code: String(s?.code || s?.nom || ''), couleurs: s?.couleurs })).filter((s) => s.nom)
    : [];
  if (!list.some((s) => s.nom === DEFAUT)) list.push({ nom: DEFAUT, code: 'PERS' });
  return list;
})();
const byNom = (nom: string) => PLANNING_STATUTS.find((s) => s.nom === nom);
const styleFor = (nom: string): React.CSSProperties => {
  const s = byNom(nom);
  return { backgroundColor: s?.couleurs?.backgroundColor || '#1e293b', color: s?.couleurs?.textColor || '#e2e8f0' };
};
const isArmable = (nom: string) => !!nom && nom !== DEFAUT;
const normGroup = (g?: string | null) => {
  const x = String(g || '').toUpperCase().replace(/^GR-?/, '').trim();
  return GROUPES.includes(x) ? x : '';
};
const emptyTrames = (): Record<string, string[]> => Object.fromEntries(GROUPES.map((g) => [g, Array(7).fill(DEFAUT)]));

type SubTab = 'trames' | 'grille' | 'repartition';

interface Props {
  buildings: Array<{ id: number; name: string }>;
}

const BuildingTrameEditor: React.FC<Props> = ({ buildings }) => {
  const [buildingId, setBuildingId] = useState<number | null>(buildings[0]?.id ?? null);
  const [trames, setTrames] = useState<Record<string, string[]>>(emptyTrames);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [subTab, setSubTab] = useState<SubTab>('trames');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // Barre « appliquer »
  const [applyGroupe, setApplyGroupe] = useState<string>('tous');
  const [applyJours, setApplyJours] = useState<string>('tous');
  const [applyStatut, setApplyStatut] = useState<string>(PLANNING_STATUTS[0]?.nom || DEFAUT);

  // Grille : édition du planning individuel, semaine par semaine
  const [weekAnchor, setWeekAnchor] = useState<Date>(() => startOfLocalDay(new Date()));
  const [savingPlanId, setSavingPlanId] = useState<number | null>(null);

  const authHeaders = useCallback((): HeadersInit => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  useEffect(() => {
    if (buildingId == null) return;
    let alive = true;
    setLoading(true);
    setMsg(null);
    (async () => {
      try {
        const res = await fetch(`${getApiBaseUrl(true)}/buildings/${buildingId}/trame`, { headers: authHeaders() });
        const data = await res.json();
        if (!alive) return;
        if (!res.ok || !data.success) throw new Error(data.error || 'Erreur de chargement');
        const merged = emptyTrames();
        for (const [g, week] of Object.entries(data.trames || {})) {
          if (Array.isArray(week) && week.length === 7) merged[g] = week.map((x) => String(x || DEFAUT));
        }
        setTrames(merged);
      } catch (err: any) {
        if (!alive) return;
        setTrames(emptyTrames());
        setMsg({ type: 'err', text: err?.message || 'Erreur réseau' });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    (async () => {
      const list = await fetchBuildingPersonnel(buildingId);
      if (alive && list) setPersonnel(list);
    })();
    return () => {
      alive = false;
    };
  }, [buildingId, authHeaders]);

  const setCell = (groupe: string, jour: number, valeur: string) =>
    setTrames((prev) => {
      const next = { ...prev, [groupe]: [...(prev[groupe] || Array(7).fill(DEFAUT))] };
      next[groupe][jour] = valeur;
      return next;
    });

  const applyBulk = () => {
    const groups = applyGroupe === 'tous' ? GROUPES : [applyGroupe];
    const days =
      applyJours === 'tous'
        ? [0, 1, 2, 3, 4, 5, 6]
        : applyJours === 'ouvre'
          ? [0, 1, 2, 3, 4]
          : applyJours === 'we'
            ? [5, 6]
            : [Number(applyJours)];
    setTrames((prev) => {
      const next = { ...prev };
      groups.forEach((g) => {
        next[g] = [...(prev[g] || Array(7).fill(DEFAUT))];
        days.forEach((d) => {
          next[g][d] = applyStatut;
        });
      });
      return next;
    });
  };

  const save = async () => {
    if (buildingId == null) return;
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/buildings/${buildingId}/trame`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ trames }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Erreur d’enregistrement');
      setMsg({ type: 'ok', text: data.message || 'Trame enregistrée' });
    } catch (err: any) {
      setMsg({ type: 'err', text: err?.message || 'Erreur réseau' });
    } finally {
      setSaving(false);
    }
  };

  const reassign = async (p: Personnel, groupe: string) => {
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/buildings/personnel/${p.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ nom: p.nom, prenom: p.prenom, groupe: groupe || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data as any).success) {
        setPersonnel((prev) => prev.map((x) => (x.id === p.id ? { ...x, groupe: groupe || null } : x)));
      }
    } catch {
      /* ignore */
    }
  };

  const membresDe = (g: string) => personnel.filter((p) => normGroup(p.groupe) === g);
  const q = filter.trim().toLowerCase();
  const filtered = personnel.filter((p) => !q || `${p.prenom} ${p.nom} ${p.grade_code || p.grade || ''}`.toLowerCase().includes(q));

  // ── Grille : planning individuel par semaine (décision qui prime sur la trame) ──
  const weekStart = useMemo(() => startOfWeekMonday(weekAnchor), [weekAnchor]);
  const weekKey = useMemo(() => toLocalDateInputValue(weekStart), [weekStart]);
  const dayColumns = useMemo(() => Array.from({ length: 7 }, (_, i) => addLocalDays(weekStart, i)), [weekStart]);
  const todayStartMs = startOfLocalDay(new Date()).getTime();

  // Semaine effective d'un agent : décision individuelle de la semaine → sinon trame de son groupe → sinon défaut.
  const effectiveWeekFor = useCallback(
    (p: Personnel): string[] => {
      const weeks = parsePlanningWeeksJson(p.planning_weeks_json);
      const stored = weeks[weekKey];
      if (stored && stored.length === 7) return stored;
      const g = normGroup(p.groupe);
      const trame = g ? trames[g] : undefined;
      if (trame && trame.length === 7) return [...trame];
      return Array(7).fill(DEFAUT);
    },
    [weekKey, trames],
  );

  const persistPlanDay = useCallback(
    async (p: Personnel, dayIndex: number, valeur: string) => {
      const days = [...effectiveWeekFor(p)];
      days[dayIndex] = valeur || DEFAUT;
      setSavingPlanId(p.id);
      setMsg(null);
      try {
        const res = await fetch(`${getApiBaseUrl(true)}/buildings/personnel/${p.id}/planning-week`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ weekStart: weekKey, days }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          setMsg({ type: 'err', text: (data as { error?: string }).error || 'Enregistrement impossible' });
          return;
        }
        setPersonnel((prev) =>
          prev.map((x) => {
            if (x.id !== p.id) return x;
            const weeks = { ...parsePlanningWeeksJson(x.planning_weeks_json) };
            weeks[weekKey] = days;
            return { ...x, planning_weeks_json: weeks };
          }),
        );
      } catch {
        setMsg({ type: 'err', text: 'Erreur réseau' });
      } finally {
        setSavingPlanId(null);
      }
    },
    [effectiveWeekFor, weekKey, authHeaders],
  );

  if (buildings.length === 0) {
    return <div className="empty">Aucun site où définir une trame.</div>;
  }

  const todayCol = (j: number): React.CSSProperties => (j === JOUR_COURANT ? { boxShadow: 'inset 0 0 0 1px rgba(56,189,248,.5)' } : {});

  return (
    <div className="block" style={{ padding: 0 }}>
      <div className="block__b">
        {buildings.length > 1 ? (
          <div className="toolbar">
            <select value={buildingId ?? ''} onChange={(e) => setBuildingId(Number(e.target.value))}>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <input type="search" placeholder="Filtrer le personnel…" value={filter} onChange={(e) => setFilter(e.target.value)} autoComplete="off" />
            {msg ? <span className={msg.type === 'ok' ? 'mono' : 'bwarn'} style={{ color: msg.type === 'ok' ? '#6ee7b7' : undefined }}>{msg.text}</span> : null}
            <button className="btn btn--sm btn--primary" type="button" disabled={saving || loading} onClick={() => void save()}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        ) : (
          <div className="toolbar">
            <input type="search" placeholder="Filtrer le personnel…" value={filter} onChange={(e) => setFilter(e.target.value)} autoComplete="off" />
            {msg ? <span style={{ fontSize: 10, color: msg.type === 'ok' ? '#6ee7b7' : '#fca5a5' }}>{msg.text}</span> : null}
            <span style={{ flex: 1 }} />
            <button className="btn btn--sm btn--primary" type="button" disabled={saving || loading} onClick={() => void save()}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        )}

        <div className="tabs tabs--drawer" style={{ marginBottom: 8 }}>
          <button className={subTab === 'trames' ? 'is-active' : ''} type="button" onClick={() => setSubTab('trames')}>Trames de groupe</button>
          <button className={subTab === 'grille' ? 'is-active' : ''} type="button" onClick={() => setSubTab('grille')}>Grille du personnel</button>
          <button className={subTab === 'repartition' ? 'is-active' : ''} type="button" onClick={() => setSubTab('repartition')}>Répartition des groupes</button>
        </div>

        {loading ? (
          <div className="empty">Chargement…</div>
        ) : subTab === 'trames' ? (
          <>
            <p className="bsub">
              Modifier une case ici change le planning de <b>tous les agents du groupe</b> qui n'ont pas de
              décision individuelle sur ce jour. C'est le moyen de replanifier cent agents d'un coup.
            </p>
            <table className="table plantable">
              <thead>
                <tr>
                  <th>Groupe</th>
                  <th>Effectif</th>
                  {JOURS.map((j, i) => (
                    <th key={j} style={todayCol(i)}>{j}</th>
                  ))}
                  <th>De garde aujourd'hui</th>
                </tr>
              </thead>
              <tbody>
                {GROUPES.map((g) => {
                  const membres = membresDe(g);
                  const armables = isArmable(trames[g]?.[JOUR_COURANT] || DEFAUT) ? membres.length : 0;
                  return (
                    <tr key={g}>
                      <td style={{ fontWeight: 800 }}>GR-{g}</td>
                      <td className="mono muted">{membres.length}</td>
                      {JOURS.map((_, j) => {
                        const val = trames[g]?.[j] || DEFAUT;
                        return (
                          <td key={j} style={todayCol(j)}>
                            <select value={val} onChange={(e) => setCell(g, j, e.target.value)} style={styleFor(val)} className="minisel" title={val}>
                              {PLANNING_STATUTS.map((s) => (
                                <option key={s.nom} value={s.nom}>{s.code}</option>
                              ))}
                            </select>
                          </td>
                        );
                      })}
                      <td className={`mono ${armables ? '' : 'muted'}`}>{armables}/{membres.length} armables</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="planbar" style={{ marginTop: 9 }}>
              <label>Groupe
                <select value={applyGroupe} onChange={(e) => setApplyGroupe(e.target.value)}>
                  <option value="tous">Tous les groupes</option>
                  {GROUPES.map((g) => (
                    <option key={g} value={g}>GR-{g} ({membresDe(g).length})</option>
                  ))}
                </select>
              </label>
              <label>Jours
                <select value={applyJours} onChange={(e) => setApplyJours(e.target.value)}>
                  <option value="tous">Semaine entière</option>
                  <option value="ouvre">Lun → Ven</option>
                  <option value="we">Sam + Dim</option>
                  {JOURS.map((j, i) => (
                    <option key={j} value={i}>{j}</option>
                  ))}
                </select>
              </label>
              <label>Statut
                <select value={applyStatut} onChange={(e) => setApplyStatut(e.target.value)}>
                  {PLANNING_STATUTS.map((s) => (
                    <option key={s.nom} value={s.nom}>{s.nom}</option>
                  ))}
                </select>
              </label>
              <button className="btn btn--sm btn--primary" type="button" onClick={applyBulk}>Appliquer</button>
              <span className="planbar__hint">Efface aussi les décisions individuelles sur ces jours</span>
            </div>
          </>
        ) : subTab === 'grille' ? (
          <>
            <div className="toolbar" style={{ marginBottom: 8 }}>
              <button className="btn btn--sm btn--ghost" type="button" onClick={() => setWeekAnchor((d) => addLocalDays(startOfWeekMonday(d), -7))}>‹ Semaine</button>
              <input
                type="date"
                value={toLocalDateInputValue(weekAnchor)}
                onChange={(e) => { if (e.target.value) setWeekAnchor(parseLocalDateInput(e.target.value)); }}
              />
              <button className="btn btn--sm btn--ghost" type="button" onClick={() => setWeekAnchor((d) => addLocalDays(startOfWeekMonday(d), 7))}>Semaine ›</button>
              <button className="btn btn--sm btn--ghost" type="button" onClick={() => setWeekAnchor(startOfLocalDay(new Date()))}>Aujourd'hui</button>
              <span className="bsub" style={{ flex: 1, textAlign: 'right' }}>{formatWeekBannerFr(weekStart)}</span>
            </div>
            <p className="bsub">
              Modifier une case pose une <b>décision individuelle</b> pour l'agent sur cette semaine : elle prime sur
              la trame de son groupe. Les jours non touchés reprennent la trame. La colonne du jour est encadrée.
            </p>
            {filtered.length === 0 ? (
              <div className="empty">Aucun agent.</div>
            ) : (
              <table className="table plantable">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Grade</th>
                    <th>GR</th>
                    {dayColumns.map((d) => {
                      const h = formatDayHeaderFr(d);
                      const today = d.getTime() === todayStartMs;
                      return (
                        <th key={d.getTime()} style={today ? { boxShadow: 'inset 0 0 0 1px rgba(56,189,248,.5)' } : undefined}>
                          <span style={{ textTransform: 'capitalize' }}>{h.weekday}</span>
                          <span className="mono muted" style={{ display: 'block', fontWeight: 400 }}>{h.dayMonth}</span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const g = normGroup(p.groupe);
                    const week = effectiveWeekFor(p);
                    const busy = savingPlanId === p.id;
                    return (
                      <tr key={p.id}>
                        <td>{`${p.prenom || ''} ${p.nom || ''}`.trim() || `Agent ${p.id}`}</td>
                        <td className="mono">{p.grade_code || p.grade || '—'}</td>
                        <td className="mono muted">{g || '—'}</td>
                        {dayColumns.map((d, j) => {
                          const val = week[j] || DEFAUT;
                          const today = d.getTime() === todayStartMs;
                          return (
                            <td key={d.getTime()} style={today ? { boxShadow: 'inset 0 0 0 1px rgba(56,189,248,.5)' } : undefined}>
                              <select
                                value={val}
                                disabled={busy}
                                onChange={(e) => void persistPlanDay(p, j, e.target.value)}
                                style={styleFor(val)}
                                className="minisel"
                                title={val}
                              >
                                {PLANNING_STATUTS.map((s) => (
                                  <option key={s.nom} value={s.nom}>{s.code}</option>
                                ))}
                              </select>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        ) : (
          <>
            <p className="bsub">Répartissez les agents entre les groupes A-D : ils héritent alors de la trame du groupe.</p>
            <div className="bsplit">
              {GROUPES.map((g) => {
                const membres = membresDe(g).filter((p) => !q || `${p.prenom} ${p.nom}`.toLowerCase().includes(q));
                return (
                  <div className="block" key={g}>
                    <div className="block__h">GR-{g} <span className="mono muted">{membresDe(g).length} agent(s)</span></div>
                    <div className="block__b">
                      {membres.length ? (
                        membres.map((p) => (
                          <div className="brecrute" key={p.id} style={{ alignItems: 'center' }}>
                            <span style={{ flex: 1, fontSize: 11 }}>{`${p.prenom || ''} ${p.nom || ''}`.trim()}</span>
                            <select value={g} onChange={(e) => reassign(p, e.target.value)} className="minisel">
                              {GROUPES.map((x) => (
                                <option key={x} value={x}>GR-{x}</option>
                              ))}
                              <option value="">— aucun —</option>
                            </select>
                          </div>
                        ))
                      ) : (
                        <div className="empty">Aucun agent.</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BuildingTrameEditor;
